// Settings → Appearance → Glass: tint, glow and blur, with a few named looks.
// Shown only while this device is on glass; the choice is per device, like the
// mode (state/glass.ts), and applies everywhere glass does.
import { HUES, tokens, GLASS, type Hue } from '@/lib/colour'
import { GLASS_BLUR, GLASS_PRESETS, presetOf } from '@/lib/glassLook'
import { useResolvedLook } from '@/db/hooks'
import { useGlassLook } from '@/state/glass'
import { SectionTitle, cx } from '@/components/ui'

const Slider = ({
  label,
  value,
  min,
  max,
  step,
  text,
  onChange,
  testid,
}: {
  label: string
  value: number
  min: number
  max: number
  step: number
  text: string
  onChange: (v: number) => void
  testid: string
}) => (
  <label className="flex flex-col justify-center min-h-14 px-3.5 py-2">
    <span className="flex items-baseline justify-between text-[15px]">
      {label}
      <span className="text-xs text-faint">{text}</span>
    </span>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="range w-full min-h-11"
      aria-valuetext={text}
      data-testid={testid}
    />
  </label>
)

export const GlassSettings = () => {
  const seen = useResolvedLook()
  const look = useGlassLook((s) => s.look)
  const set = useGlassLook((s) => s.set)
  // a look that is not glass has nothing to adjust
  if (!seen.glass || seen.fixed) return null
  const preset = presetOf(look)
  return (
    <section data-testid="glass-settings">
      <SectionTitle>Glass</SectionTitle>
      <p className="text-xs text-faint px-1 mb-2">
        The glow behind the panels and how frosted they are. This device only.
      </p>
      <ul className="group-list mb-3" role="radiogroup" aria-label="Glass looks">
        {GLASS_PRESETS.map((p) => {
          const on = preset?.key === p.key
          return (
            <li key={p.key}>
              <button
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => set(p.look)}
                className="w-full flex items-center gap-3 px-3.5 min-h-14 py-2 text-left hover:bg-raised/50"
                data-testid={`glass-preset-${p.key}`}
              >
                <span
                  className="h-6 w-6 shrink-0 rounded-full border border-line-strong"
                  style={{
                    background:
                      p.look.tint === 'suite'
                        ? 'linear-gradient(135deg, #c4b5fd, #7dd3fc)'
                        : tokens(p.look.tint as Hue).accent,
                  }}
                  aria-hidden
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-[15px]">{p.label}</span>
                  <span className="block text-xs text-faint">{p.hint}</span>
                </span>
                <span
                  className={cx(
                    'h-5 w-5 shrink-0 rounded-full border-2',
                    on
                      ? 'border-accent bg-accent shadow-[inset_0_0_0_3px_var(--color-panel)]'
                      : 'border-line-strong',
                  )}
                  aria-hidden
                />
              </button>
            </li>
          )
        })}
      </ul>
      <div className="group-list">
        <div className="px-3.5 py-2">
          <div className="text-[15px] mb-1">Tint</div>
          <div className="flex flex-wrap gap-1" role="radiogroup" aria-label="Glow colour">
            <button
              type="button"
              role="radio"
              aria-checked={look.tint === 'suite'}
              onClick={() => set({ tint: 'suite' })}
              className={cx(
                'min-h-11 rounded-full border px-3.5 text-sm',
                look.tint === 'suite' ? 'border-accent bg-accent-soft text-fg' : 'border-line text-muted',
              )}
              data-testid="glass-tint-suite"
            >
              Suite colour
            </button>
            {HUES.map((h) => (
              <button
                key={h}
                type="button"
                role="radio"
                aria-checked={look.tint === h}
                aria-label={h}
                onClick={() => set({ tint: h })}
                className="h-11 w-11 flex items-center justify-center"
                data-testid={`glass-tint-${h}`}
              >
                <span
                  className={cx(
                    'h-7 w-7 rounded-full border-2',
                    look.tint === h ? 'border-fg' : 'border-transparent',
                  )}
                  style={{ background: tokens(h).accent }}
                />
              </button>
            ))}
          </div>
        </div>
        <Slider
          label="Glow"
          value={look.glow}
          min={0}
          max={GLASS.glowMax}
          step={0.01}
          text={`${Math.round((look.glow / GLASS.glowMax) * 100)}%`}
          onChange={(glow) => set({ glow })}
          testid="glass-glow"
        />
        <Slider
          label="Blur"
          value={look.blur}
          min={GLASS_BLUR.min}
          max={GLASS_BLUR.max}
          step={1}
          text={`${look.blur} px`}
          onChange={(blur) => set({ blur })}
          testid="glass-blur"
        />
      </div>
    </section>
  )
}
